import React, { useState, useEffect } from "react";
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import { PenLine, Loader2, ChevronDown, Plus, Trash2, UserCircle } from "lucide-react";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import SignatorySplash from "@/components/drill/SignatorySplash";

export default function SignatureSection({ sheet }) {
  const [signatories, setSignatories] = useState([]);
  const [splashOpen, setSplashOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!sheet) return;
    if (Array.isArray(sheet.signatories) && sheet.signatories.length) {
      setSignatories(sheet.signatories);
    } else if (sheet.signatory_1_name) {
      setSignatories([
        {
          name: sheet.signatory_1_name,
          role: sheet.signatory_1_role || "",
          signature: sheet.signatory_1_signature || "",
        },
      ]);
    } else {
      setSignatories([]);
    }
  }, [sheet?.id]);

  const persist = async (next) => {
    setSaving(true);
    try {
      await base44.entities.Sheet.update(sheet.id, { signatories: next });
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async (data) => {
    const next = [
      ...signatories,
      { name: data.name, role: data.role, signature: data.signature },
    ];
    setSignatories(next);
    setSplashOpen(false);
    await persist(next);
  };

  const handleRemove = async (idx) => {
    const next = signatories.filter((_, i) => i !== idx);
    setSignatories(next);
    await persist(next);
  };

  return (
    <Collapsible className="bg-white rounded-2xl border border-slate-200 mt-6 dark:bg-slate-900 dark:border-slate-800">
      <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 p-4 hover:bg-slate-50 dark:hover:bg-slate-800/60 rounded-2xl transition-colors">
        <div className="flex items-center gap-2">
          <PenLine className="w-5 h-5 text-slate-600 dark:text-slate-300" />
          <h3 className="font-semibold text-slate-900 dark:text-slate-100">Signatures</h3>
        </div>
        <ChevronDown className="w-4 h-4 text-slate-500 dark:text-slate-400 transition-transform duration-200 [[data-state=open]>&]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-4 pb-4 space-y-4">
          {signatories.length > 0 ? (
            <div className="space-y-2">
              {signatories.map((s, idx) => (
                <div
                  key={idx}
                  className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 p-3 dark:border-slate-800"
                >
                  <div className="flex items-start gap-3">
                    <UserCircle className="w-8 h-8 text-slate-400 shrink-0" />
                    <div>
                      <p className="font-medium text-slate-900 dark:text-slate-100">{s.name}</p>
                      {s.role && (
                        <p className="text-sm text-slate-500 dark:text-slate-400">{s.role}</p>
                      )}
                      {s.signature && (
                        <p className="text-sm italic text-slate-600 dark:text-slate-300 mt-1 font-serif">
                          {s.signature}
                        </p>
                      )}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemove(idx)}
                    className="text-red-500 hover:text-red-600 h-11 w-11"
                    title="Remove signatory"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500 dark:text-slate-400">No signatories added yet.</p>
          )}

          <Button
            type="button"
            onClick={() => setSplashOpen(true)}
            disabled={saving}
            className="rounded-xl bg-slate-900 hover:bg-slate-800 gap-2 w-full"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add Signature
          </Button>
        </div>
      </CollapsibleContent>

      <SignatorySplash open={splashOpen} onOpenChange={setSplashOpen} onSave={handleAdd} />
    </Collapsible>
  );
}